import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { theme } from '../../../tailwind.config';
import { ArrowDownComponent as ArrowDownIcon } from 'ui/Icons';
import classnames from 'classnames';
import { useHideOnClickOutside } from 'hooks/useHideOnClickOutside';
import { SvgIcon } from 'ui/SvgIcon';
import LoaderIcon from 'ui/Icons/tabler/loader.component.svg';
import { TextInput } from 'ui/TextInput';
import { IMultiSelectWithSearchOption, MultiSelectOption } from './MultiSelectOption';
import { ClearCircle } from 'ui/Icons/components/Clear.component';

export enum EMultiSelectWithSearchDirection {
  UP = 'up',
  DOWN = 'down',
}

export interface IMultiSelectWithSearchProps {
  value: string[];
  options: IMultiSelectWithSearchOption[];
  onChange?: (value: string[]) => void;
  label?: string;
  fieldId: string; // used in conjunction with label
  maxVisibleItems?: number;
  showSelectAllOption?: boolean;
  allowGrouping?: boolean;
  allowSorting?: boolean;
  allowFiltering?: boolean;
  direction?: EMultiSelectWithSearchDirection;
  customLabelWhenNothingSelected?: string;
  loading?: boolean;
  disabled?: boolean;
  applyChangesOnClose?: boolean; // when true, onChange is called only when dropdown closes
  // notify when dropdown is opened or closed (advanced scenarios)
  notifyOpenStatus?: (isOpen: boolean) => void;
  // showing error messages
  errorMessage?: string | null;
  errorClassName?: string;
  // class names for styling
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  showMultipleSelectionNames?: boolean;
}

const sortingAlphabetically = (a: IMultiSelectWithSearchOption, b: IMultiSelectWithSearchOption) => (a.label?.trim() || '').localeCompare(b.label?.trim() || '');

const ITEM_HEIGHT = 35;

interface IVirtualListProps {
  options: IMultiSelectWithSearchOption[];
  selectedSet: Set<string>;
  onSelect: (optionId: string) => void;
  maxVisibleItems?: number;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

const VirtualList: React.FC<IVirtualListProps> = ({ options, selectedSet, onSelect, maxVisibleItems, scrollContainerRef }) => {
  const scrollContainerInitialHeight = maxVisibleItems ? maxVisibleItems * ITEM_HEIGHT : 350;

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 50,
    initialRect: { width: 0, height: scrollContainerInitialHeight },
  });

  return (
    <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
      {virtualizer.getVirtualItems().map(virtualItem => {
        const option = options[virtualItem.index];
        return (
          <div
            key={String(virtualItem.key)}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            className="absolute top-0 left-0 w-full"
            style={{ transform: `translateY(${virtualItem.start}px)` }}
          >
            <MultiSelectOption
              option={option}
              isSelected={selectedSet.has(option.value)}
              onSelect={onSelect}
            />
          </div>
        );
      })}
    </div>
  );
};

export const MultiSelectWithSearch: React.FC<IMultiSelectWithSearchProps> = React.memo(
  ({
    fieldId,
    value: selectedValues,
    options,
    onChange,
    label,
    maxVisibleItems,
    direction = EMultiSelectWithSearchDirection.DOWN,
    customLabelWhenNothingSelected,
    className = '',
    inputClassName = '',
    labelClassName = '',
    loading = false,
    notifyOpenStatus,
    errorMessage,
    errorClassName,
    disabled = false,
    showSelectAllOption = false,
    allowGrouping = false,
    allowSorting = false,
    allowFiltering = false,
    applyChangesOnClose = false,
    showMultipleSelectionNames = false,
  }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [innerOptions, setInnerOptions] = useState<IMultiSelectWithSearchOption[]>(options);
    const [filterText, setFilterText] = useState<string>('');
    const [pendingValues, setPendingValues] = useState<string[]>(selectedValues);
    const prevIsOpenRef = useRef<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const notifyOnClickOutside = useCallback(() => {
      setFilterText('');
      notifyOpenStatus && notifyOpenStatus(false);
    }, [notifyOpenStatus]);
    const { instanceClassname } = useHideOnClickOutside(setIsOpen, 'select', notifyOnClickOutside);

    // Sync pending values with selected values when external value changes
    useEffect(() => {
      setPendingValues(selectedValues);
    }, [selectedValues]);

    // Call onChange when dropdown closes if applyChangesOnClose is true, but only if the value actually changed
    useEffect(() => {
      if (applyChangesOnClose && prevIsOpenRef.current && !isOpen && onChange) {
        const hasChanged =
          pendingValues.length !== selectedValues.length ||
          !pendingValues.every(v => selectedValues.includes(v));
        if (hasChanged) {
          onChange(pendingValues);
        }
      }
    }, [isOpen, applyChangesOnClose, onChange, pendingValues, selectedValues]);

    const activeValues = applyChangesOnClose && isOpen ? pendingValues : selectedValues;

    const selectedSet = useMemo(() => new Set(activeValues), [activeValues]);

    const selectedOptions = useMemo(
      () => options.filter(item => item.value && selectedValues.includes(item.value)),
      [options, selectedValues]
    );

    // When opening with 0 or multiple selections, scroll virtual list back to top
    useEffect(() => {
      if (allowFiltering && isOpen && !prevIsOpenRef.current && activeValues.length !== 1) {
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
      }
    }, [allowFiltering, isOpen, activeValues.length]);

    // Show selected items first when dropdown opens
    useEffect(() => {
      if (isOpen && !prevIsOpenRef.current) {
        let orderedOptions = [...options];

        if (allowGrouping) {
          const selected = orderedOptions.filter(opt => opt.value && activeValues.includes(opt.value));
          const unselected = orderedOptions.filter(opt => !opt.value || !activeValues.includes(opt.value));

          if (allowSorting) {
            selected.sort(sortingAlphabetically);
            unselected.sort(sortingAlphabetically);
          }

          orderedOptions = [...selected, ...unselected];
        } else if (allowSorting) {
          orderedOptions.sort(sortingAlphabetically);
        }

        setInnerOptions(orderedOptions);
      }

      prevIsOpenRef.current = isOpen;
    }, [isOpen, options, activeValues, allowGrouping, allowSorting]);

    const filteredOptions = useMemo(() => {
      if (!allowFiltering || !filterText) return innerOptions;
      const lower = filterText.toLowerCase();
      return innerOptions.filter(option => (option.label || '').toLowerCase().includes(lower));
    }, [innerOptions, allowFiltering, filterText]);

    const toggle = useCallback(() => {
      if (!disabled && !loading) {
        setIsOpen(!isOpen);
        if (isOpen) {
          setFilterText('');
        }
      }
      notifyOpenStatus && notifyOpenStatus(!isOpen);
    }, [isOpen, notifyOpenStatus, disabled, loading]);

    const handleSelectAll = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!onChange) return;
      const allValues = options.map(opt => opt.value).filter(Boolean) as string[];
      if (applyChangesOnClose) {
        setPendingValues(allValues);
      } else {
        onChange(allValues);
      }
    }, [onChange, options, applyChangesOnClose]);

    const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setFilterText((e.target as HTMLInputElement).value);
    }, []);

    const handleClearSelection = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
      if (!onChange) return;
      e.stopPropagation();
      setPendingValues([]);
      onChange([]);
    }, [onChange]);

    const handleSelect = useCallback(
      (optionId: string) => {
        if (!onChange) return;
        if (!optionId) return;

        const currentValues = applyChangesOnClose ? pendingValues : selectedValues;
        const newValues = currentValues.includes(optionId)
          ? currentValues.filter(v => v !== optionId)
          : [...currentValues, optionId];

        if (applyChangesOnClose) {
          setPendingValues(newValues);
        } else {
          onChange(newValues);
        }
      },
      [onChange, selectedValues, pendingValues, applyChangesOnClose]
    );

    const renderSelectedOption = (selectedOption?: IMultiSelectWithSearchOption) => {
      if (selectedOption?.renderItem) {
        return (
          <div className="pl-[10px] pr-[20px]" style={{ width: 'calc(100% - 45px)' }}>
            {selectedOption?.renderItem(true)}
          </div>
        );
      }
      return (
        <span
          className={classnames('font-pt-sans px-10px py-2 text-15px leading-19px whitespace-nowrap select-none inline overflow-hidden text-ellipsis', {
            'text-gray-100': disabled,
            'text-black': !disabled,
          })}
          style={{ maxWidth: 'calc(100% - 60px)' }}
        >
          {selectedOption?.label}
        </span>
      );
    };

    const renderMultiSelectDisplay = (selectedOptions: IMultiSelectWithSearchOption[], isOpen: boolean) => {
      if (isOpen) {
        return (
          <span className="font-pt-sans px-10px py-2 select-none text-15px leading-19px"></span>
        );
      }

      if (selectedOptions.length === 0) {
        return (
          <span
            className={classnames('font-pt-sans px-10px py-2 select-none text-15px leading-19px', {
              'text-black': !!customLabelWhenNothingSelected,
              'text-gray-100': !customLabelWhenNothingSelected,
            })}
          >
            {customLabelWhenNothingSelected ?? 'Select ...'}
          </span>
        );
      }

      if (selectedOptions.length === 1) {
        return renderSelectedOption(selectedOptions[0]);
      }

      if (showMultipleSelectionNames) {
        return (
          <span
            className={classnames({
              'font-pt-sans px-10px py-2 text-15px leading-19px whitespace-nowrap select-none overflow-hidden text-ellipsis pr-[57px]': true,
              'text-gray-100': disabled,
              'text-black': !disabled,
            })}
          >
            {selectedOptions.map(option => option.label).join(', ')}
          </span>
        );
      }

      return (
        <span
          className={classnames({
            'font-pt-sans px-10px py-2 text-15px leading-19px whitespace-nowrap select-none flex overflow-hidden text-ellipsis': true,
            'text-gray-100': disabled,
            'text-black': !disabled,
          })}
          style={{ maxWidth: 'calc(100% - 45px)' }}
        >
          Multiple ({selectedOptions.length})
        </span>
      );
    };

    const scrollContainerMaxHeight = maxVisibleItems ? `${maxVisibleItems * ITEM_HEIGHT}px` : '350px';

    return (
      <div
        className={classnames(
          instanceClassname,
          'multiselect-with-search flex flex-col min-w-75px focus-visible:outline-none focus-visible:shadow-shadow-marine',
          className
        )}
      >
        {label && (
          <label
            htmlFor={fieldId}
            className={classnames(
              labelClassName,
              'font-pt-sans mb-5px default:text-black default:text-13px default:leading-17px default:tracking-2xs default:select-none'
            )}
          >
            {label}
          </label>
        )}

        <div
          id={fieldId}
          className={classnames(
            'relative flex w-full cursor-pointer items-center justify-between text-15px h-[35px]',
            inputClassName,
            {
              'border border-solid': !disabled,
              'border-gray-40': !errorMessage,
              'border-red-95 bg-red-25': !!errorMessage,
              'bg-ivory': !errorMessage && !disabled,
              'bg-gray-10': disabled,
            }
          )}
          onClick={toggle}
        >
          <div className="options flex items-center justify-start relative h-[33px] w-full">
            {!loading && renderMultiSelectDisplay(selectedOptions, isOpen)}
            {loading && (
              <div className="flex justify-center items-center min-h-35px" style={{ width: 'calc(100% - 30px)' }}>
                <SvgIcon IconComponent={LoaderIcon} className="w-[18px] h-[18px] fill-transparent stroke-gray-80 stroke-2 animate-spin" />
              </div>
            )}

            <div
              className={classnames('font-pt-sans absolute -left-1px z-30 w-full border border-solid border-gray-40 bg-ivory', {
                hidden: !isOpen,
                'top-[35px] border-t-0': direction === EMultiSelectWithSearchDirection.DOWN,
                'bottom-[34px] border-b-0': direction === EMultiSelectWithSearchDirection.UP,
              })}
              style={{ width: 'calc(100% + 2px)' }}
            >
              <div
                ref={containerRef}
                className="options-container flex flex-col pb-[14px] overflow-y-auto select-none"
                style={{ maxHeight: scrollContainerMaxHeight }}
              >
                {!loading && allowFiltering && (
                  <div className="px-[5px] pt-[5px]" onClick={e => e.stopPropagation()}>
                    <TextInput
                      id={`${fieldId}-filter`}
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder="Search here ..."
                      inputClassName="h-[35px] py-0 mt-0 text-gray-100 placeholder:text-gray-100"
                      className="mb-0"
                    />
                  </div>
                )}
                {!loading && showSelectAllOption && (
                  <div className="flex items-center h-[35px] mt-[10px] px-10px">
                    <button
                      type="button"
                      className="text-13px font-pt-sans text-brown-prime underline decoration-solid cursor-pointer bg-transparent border-none p-0 m-0"
                      onClick={handleSelectAll}
                    >
                      All
                    </button>
                  </div>
                )}
                {!loading && isOpen && (
                  <VirtualList
                    options={filteredOptions}
                    selectedSet={selectedSet}
                    onSelect={handleSelect}
                    maxVisibleItems={maxVisibleItems}
                    scrollContainerRef={containerRef}
                  />
                )}
              </div>
            </div>
          </div>

          <span className="arrow-down absolute top-0 right-0 border-l border-solid border-l-gray-20 flex h-full w-35px items-center justify-center">
            <ArrowDownIcon
              icon={theme.colors['gray-80']}
              className={classnames('transition-rotate duration-500 ease-in-out', 'fill-gray-80', {
                'rotate-180': isOpen,
                'rotate-0': !isOpen,
              })}
            />
          </span>
          {!loading && selectedOptions.length > 0 && !isOpen && (
            <span className="clear-selection absolute top-0 right-[35px] flex h-full w-35px items-center justify-center" onClick={handleClearSelection}>
              <SvgIcon IconComponent={ClearCircle} className="w-5 h-5 fill-gray-100 hover:fill-teal-100 cursor-pointer relative" />
            </span>
          )}
        </div>

        {errorMessage && (
          <p className={`error-message text-red-95 text-13px leading-2xs font-pt-sans mt-5px mb-0 ${errorClassName ?? ''}`}>
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);
